import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { type ForwardedRef, forwardRef, useState } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

import { VisibilityOutlinedIcon, VisibilityOffOutlinedIcon } from '../../icons';
import IconButton from '../icon-button';

type FormInputProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  React.ComponentProps<'input'> & {
    label?: string;
    radios?: JSX.Element;
    required?: boolean;
  };

export function InputInner<TFieldValues extends FieldValues = FieldValues>(
  {
    id,
    label,
    type = 'text',
    radios,
    placeholder = '',
    name,
    required,
    disabled,
    className,
    ...props
  }: FormInputProps<TFieldValues>,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const {
    field,
    fieldState: { error },
  } = useController({ ...props, name, rules: { required, ...props.rules } });

  const { t } = useTranslation();
  const [passwordVisible, setPasswordVisible] = useState(false);

  const passwordFieldType = passwordVisible ? 'text' : 'password';

  const isRadio = type === 'radio';
  const Container = isRadio ? 'div' : 'label';
  const isError = props['aria-invalid'] === 'true';
  return (
    <Container htmlFor={name}>
      {label ? (
        <div
          className={cn(
            'mb-2 text-start text-sm font-semibold',
            required && 'after:text-danger after:content-["*"]',
          )}
        >
          {label}
        </div>
      ) : null}
      <div className={cn('relative', isRadio && 'grid gap-4')}>
        {!isRadio ? (
          <>
            <input
              {...field}
              ref={ref}
              id={id}
              type={type === 'password' ? passwordFieldType : type}
              placeholder={placeholder}
              disabled={disabled}
              className={cn(
                /*
              `outline-color` not working in Safari
              'px-4 py-3 w-full rounded-lg border border-[#0000001f] outline-primary',
              isError ? 'border-danger outline-danger' : '',
              */
                'focus:border-primary w-full rounded-md border-2 border-[#0000001f] px-4 py-2.5 outline-none',
                disabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                error || isError
                  ? 'border-danger focus:border-danger focus:shadow-as-border-danger'
                  : '',
                className,
              )}
              {...props}
            />
            {error?.message ? (
              <p className='text-danger mt-2 text-start text-sm'>{t(error.message)}</p>
            ) : null}
            {type === 'password' && (
              <IconButton
                className='absolute top-1 right-1.5'
                onClick={() => setPasswordVisible((prev) => !prev)}
              >
                {passwordVisible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
              </IconButton>
            )}
          </>
        ) : null}
        {radios}
      </div>
    </Container>
  );
}

export const Input = forwardRef(InputInner) as <TFieldValues extends FieldValues>(
  props: FormInputProps<TFieldValues> & { ref?: ForwardedRef<HTMLInputElement> },
) => React.ReactElement;

type FormTextareaInputProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  React.ComponentProps<'textarea'> & {
    label?: string;
    radios?: JSX.Element;
    required?: boolean;
  };

export function Textarea<TFieldValues extends FieldValues = FieldValues>({
  id,
  label,
  placeholder = '',
  name,
  required,
  className,
  ...props
}: FormTextareaInputProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController({ ...props, name, rules: { required, ...props.rules } });
  const isError = props['aria-invalid'] === 'true';

  return (
    <label htmlFor={name}>
      {label ? <div className='mb-2 text-start text-sm font-bold'>{label}</div> : null}
      <div className='grid gap-4'>
        <textarea
          {...field}
          id={id}
          placeholder={placeholder}
          className={cn(
            /*
              `outline-color` not working in Safari
              'px-4 py-3 w-full rounded-lg border border-[#0000001f] outline-primary',
              isError ? 'border-danger outline-danger' : '',
              */
            'focus:border-primary focus:shadow-as-border w-full rounded-lg border border-[#0000001f] px-4 py-3 outline-none',
            error || isError
              ? 'border-danger focus:border-danger focus:shadow-as-border-danger'
              : '',
            className,
          )}
          {...props}
        />
      </div>
    </label>
  );
}

type CustomFormRadioProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  Omit<React.ComponentProps<'label'>, 'children'> & {
    value?: string;
    children: React.ReactNode | ((isChecked: boolean) => React.ReactNode);
    required?: boolean;
  };

export function Radio<TFieldValues extends FieldValues = FieldValues>({
  id,
  name,
  value,
  children,
  required = false,
  className,
  ...props
}: CustomFormRadioProps<TFieldValues>) {
  const isError = props['aria-invalid'] === 'true';
  const {
    field,
    fieldState: { error },
  } = useController({ ...props, name, rules: { required, ...props.rules } });

  const isChecked = value === field.value;

  return (
    <div>
      <input
        {...field}
        type='radio'
        id={id}
        value={value}
        className='peer hidden'
        checked={value === field.value}
      />
      <label
        htmlFor={id}
        className={cn(
          'xs:px-5 grid h-full w-full !cursor-pointer place-items-center rounded-lg px-3 py-4 text-center hover:bg-gray-50',
          isError || error ? 'border-danger peer-checked:border-danger' : 'border-gray-200',
          className,
        )}
        {...props}
      >
        {typeof children === 'function' ? children(isChecked) : children}
      </label>
    </div>
  );
}

type FormCheckboxProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  React.ComponentProps<'input'> & {
    children: JSX.Element | string;
    required?: boolean;
  };

export function Checkbox<TFieldValues extends FieldValues = FieldValues>({
  id,
  checked,
  children,
  disabled = false,
  name,
  required,
  className,
  ...props
}: FormCheckboxProps<TFieldValues>) {
  const {
    field: { ...field },
  } = useController({ ...props, name, rules: { required, ...props.rules } });
  return (
    <div className='flex items-center gap-2'>
      <label htmlFor={id} className='inline-flex cursor-pointer items-center space-x-2'>
        <input
          {...field}
          type='checkbox'
          id={id}
          checked={checked}
          disabled={disabled}
          className='peer hidden'
          {...props}
        />
        <div
          className={cn(
            'flex h-[18px] w-[18px] items-center justify-center rounded-xs border-2',
            disabled
              ? 'border-gray-300 peer-checked:bg-gray-300 checked:peer-checked:bg-gray-300'
              : 'peer-checked:bg-primary peer-checked:border-primary border-gray-400',
            className,
          )}
        >
          <svg
            className='text-white'
            fill='none'
            stroke='currentColor'
            strokeWidth='3'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='square' strokeLinejoin='round' d='M5 13l4 4L19 7' />
          </svg>
        </div>
        <div className={cn(['text-body1 cursor-pointer', disabled && 'text-gray-300'])}>
          {children}
        </div>
      </label>
    </div>
  );
}

type FormCustomCheckboxProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> &
  Omit<React.ComponentProps<'input'>, 'children'> & {
    children: (params: { open: boolean }) => React.ReactNode | string;
    required?: boolean;
  };

export function CustomCheckbox<TFieldValues extends FieldValues = FieldValues>({
  id,
  children,
  disabled = false,
  name,
  required,
  className,
  ...props
}: FormCustomCheckboxProps<TFieldValues>) {
  const {
    field: { ...field },
  } = useController({ ...props, name, rules: { required, ...props.rules } });

  return (
    <label className='grid cursor-pointer place-items-center'>
      <input
        {...field}
        type='checkbox'
        id={id}
        checked={field.value}
        className={cn(['peer hidden appearance-none', className])}
        disabled={disabled}
        {...props}
      />
      {children({ open: field.value })}
    </label>
  );
}
